import { onBeforeUnmount, watch } from "vue"
import { useAuthStore } from "@/stores/auth"
import { useConsumerAuthStore } from "@/stores/consumerAuth"
import { useNotificationsStore } from "@/stores/notifications"
import { subscribeInboxChanged } from "@/utils/inboxEvents"

const baseURL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api/v1"

/**
 * SSE inbox (Bearer fetch + reader) + sinkron Pinia - pola kerja seperti Pataniku
 * (satu composable global, reconnect, polling cadangan). EventSource tidak dipakai
 * karena backend VideoStreaming memakai Authorization header.
 */
export function useNotificationStream(): void {
  const auth = useAuthStore()
  const consumer = useConsumerAuthStore()
  const store = useNotificationsStore()

  let abortRef: AbortController | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let pollTimer: ReturnType<typeof setInterval> | null = null
  let unsubInbox: (() => void) | null = null

  function effectiveToken(): string | null {
    // Hanya token aktif dari Pinia — jangan baca localStorage langsung (bisa expired/stale).
    return auth.token || consumer.token
  }

  function handleAuthFailure(usedToken: string): void {
    if (auth.token === usedToken) {
      void auth.logout()
    } else if (consumer.token === usedToken) {
      consumer.logoutLocal()
    }
    store.reset()
  }

  function clearTimers(): void {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (pollTimer != null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  function stopLoop(): void {
    abortRef?.abort()
    abortRef = null
  }

  function scheduleReconnect(delayMs = 3000): void {
    if (reconnectTimer != null) return
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void runLoop()
    }, delayMs)
  }

  async function runLoop(): Promise<void> {
    const token = effectiveToken()
    if (!token) return

    stopLoop()
    const ac = new AbortController()
    abortRef = ac

    try {
      const res = await fetch(`${baseURL}/notifications/stream`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        credentials: "include",
        cache: "no-store",
        signal: ac.signal,
      })
      if (res.status === 401 || res.status === 403) {
        handleAuthFailure(token)
        return
      }
      if (!res.ok || !res.body) {
        scheduleReconnect(10_000)
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ""
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const parts = buf.split("\n\n")
        buf = parts.pop() ?? ""
        for (const block of parts) {
          for (const line of block.split("\n")) {
            if (!line.startsWith("data: ")) continue
            try {
              const json = JSON.parse(line.slice(6)) as { event?: string }
              if (json.event === "connected" || json.event === "inbox_changed") {
                void store.peekRefreshAfterRemoteEvent()
              }
            } catch {
              /* abaikan */
            }
          }
        }
      }
    } catch (e) {
      if ((e as Error).name === "AbortError") return
    }
    if (!effectiveToken()) return
    scheduleReconnect(10_000)
  }

  function startPolling(): void {
    if (pollTimer != null) return
    pollTimer = setInterval(() => {
      if (!effectiveToken()) return
      void store.refreshUnread()
    }, 60_000)
  }

  const stopWatch = watch(
    () => [auth.isAuthenticated, consumer.isAuthenticated] as const,
    ([adminOk, portalOk]) => {
      clearTimers()
      stopLoop()
      if (adminOk || portalOk) {
        void store.refreshUnread()
        void runLoop()
        startPolling()
      } else {
        store.reset()
      }
    },
    { immediate: true },
  )

  unsubInbox = subscribeInboxChanged(() => {
    void store.peekRefreshAfterRemoteEvent()
  })

  onBeforeUnmount(() => {
    stopWatch()
    unsubInbox?.()
    unsubInbox = null
    clearTimers()
    stopLoop()
  })
}
